import { ServiceProvider } from '../app/PlundrComponents/types/PartnerTypes';

/**
 * IMPORTANT NOTE ON SOCIAL MEDIA SHARING LIMITATIONS:
 * 
 * The OG and Twitter meta tags set by this utility will NOT be visible to social media crawlers
 * when they visit the page. This is because social media crawlers do not execute JavaScript and
 * only see the initial HTML tags in the document.
 * 
 * For proper social media sharing with dynamic partner information, consider one of these approaches:
 * 
 * 1. Server-Side Rendering (SSR): Convert the app to use Next.js or similar SSR framework
 * 2. Prerendering Service: Use a service like Prerender.io that serves pre-rendered HTML to crawlers
 * 3. Edge Middleware (if using Vercel): Intercept requests from social media crawlers and serve
 *    custom HTML with the correct meta tags
 * 4. Dynamic Meta Tags API: Create a server endpoint that generates HTML with the correct meta tags
 *    based on the URL parameters
 * 
 * The current implementation is useful for SEO with search engines that execute JavaScript (like Google),
 * but will not work for social media sharing on platforms like Twitter, Facebook, LinkedIn, etc.
 */

/**
 * Updates the document's meta tags for SEO based on partner information
 * @param partner The partner data to use for SEO tags
 */
export const updateSEOTags = (partner: ServiceProvider | null, customLogo: string | null, customBrandingImage: string | null): void => {
  if (!partner) return;

  // Get business name or display name
  const businessName = partner.businessProfile?.businessName || partner.display_name || 'HoSaksham';
  
  // Create description from partner data
  const description = partner.plundrDescription || 
    `${businessName} - Manage your subscriptions and grow your business with HoSaksham's Revenue Growth Management platform.`;
  
  // Get partner image or default to logo
  const imageUrl = customLogo || customBrandingImage || 'https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FOg%20image.png?alt=media&token=6908d97d-8f83-4875-9508-e1e595889f95';
    
  // Generate keywords based on partner data
  const keywords = generateKeywords(partner, businessName);

  // Update title tags
  document.title = `${businessName} | HoSaksham`;
  updateMetaTag('title', `${businessName} | HoSaksham`);
  
  // Update description tags
  updateMetaTag('description', description);
  
  // Update keywords
  updateMetaTag('keywords', keywords);
  
  // Update Open Graph tags
  updateMetaTag('og:title', `${businessName} | HoSaksham`);
  updateMetaTag('og:description', description);
  updateMetaTag('og:image', imageUrl);
  updateMetaTag('og:url', window.location.href);
  updateMetaTag('og:site_name', 'HoSaksham');
  updateMetaTag('og:type', 'website');
  
  // First, remove any existing Twitter meta tags to avoid conflicts
  removeExistingTwitterTags();
  
  // Update Twitter tags with proper attributes
  // Twitter uses both name and property attributes in different implementations
  addTwitterTag('twitter:card', 'summary_large_image');
  addTwitterTag('twitter:title', `${businessName} | HoSaksham`);
  addTwitterTag('twitter:description', description);
  addTwitterTag('twitter:image', imageUrl);
  addTwitterTag('twitter:url', window.location.href);
  addTwitterTag('twitter:site', '@HoSaksham');
  addTwitterTag('twitter:creator', '@HoSaksham');
  
  // Update canonical URL
  updateCanonicalUrl(window.location.href);
  
  // Update favicon/icon
  updateFavicon(customLogo || 'https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2Ffavicon.ico?alt=media&token=6908d97d-8f83-4875-9508-e1e595889f95');
  
  // Add structured data
  addStructuredData(partner, businessName, description, imageUrl);
};

/**
 * Generates SEO keywords based on partner data
 * @param partner Partner data
 * @param businessName Business name
 * @returns Comma-separated keywords string
 */
const generateKeywords = (partner: ServiceProvider, businessName: string): string => {
  const baseKeywords = [
    businessName,
  ];
  
  // Add service types if available
  if (partner.ServiceTypes && partner.ServiceTypes.length > 0) {
    partner.ServiceTypes.forEach(serviceType => {
      baseKeywords.push(serviceType);
      baseKeywords.push(`${serviceType} subscription`);
      baseKeywords.push(`${serviceType} management`);
    });
  }
  
  // Add business-specific keywords
  if (businessName !== 'HoSaksham') {
    baseKeywords.push(`${businessName} subscriptions`);
    baseKeywords.push(`${businessName} services`);
  }
  
  // Remove duplicates and join with commas
  return [...new Set(baseKeywords)].join(', ');
};

/**
 * Helper function to update or create a meta tag
 * @param name The name or property attribute of the meta tag
 * @param content The content to set
 */
const updateMetaTag = (name: string, content: string): void => {
  // Check if the tag uses name or property attribute
  const isProperty = name.startsWith('og:') || name.startsWith('twitter:');
  const attr = isProperty ? 'property' : 'name';
  
  // Try to find existing tag
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  
  // Create tag if it doesn't exist
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  
  // Update content
  tag.setAttribute('content', content);
};

/**
 * Updates the canonical URL
 * @param url The URL to set as canonical
 */
const updateCanonicalUrl = (url: string): void => {
  // Try to find existing canonical link
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  
  // Create if it doesn't exist
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  
  // Update href
  canonicalLink.setAttribute('href', url);
};

/**
 * Adds structured data (JSON-LD) for better SEO
 * @param partner Partner data
 * @param businessName Business name
 * @param description Business description
 * @param imageUrl Business image URL
 */
const addStructuredData = (
  partner: ServiceProvider,
  businessName: string,
  description: string,
  imageUrl: string
): void => {
  // Remove any existing JSON-LD script
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Create structured data for local business
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': businessName,
    'description': description,
    'image': imageUrl,
    'url': window.location.href,
    'telephone': partner.phone_number || '',
    'email': partner.email || '',
    'aggregateRating': partner.plundrRating && partner.plundrRating.length > 0 ? {
      '@type': 'AggregateRating',
      'ratingValue': partner.plundrRating.reduce((acc, curr) => acc + curr.rating, 0) / partner.plundrRating.length,
      'ratingCount': partner.plundrRating.length
    } : undefined
  };
  
  // Create and add the script element
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

/**
 * Updates the favicon/icon of the website
 * @param iconUrl The URL of the icon to use
 */
const updateFavicon = (iconUrl: string): void => {
  // Try to find existing favicon links
  const faviconSelectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]'
  ];
  
  faviconSelectors.forEach(selector => {
    // Try to find existing link
    let linkElement = document.querySelector(selector);
    
    // Create if it doesn't exist
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', selector.match(/rel="([^"]+)"/)?.[1] || 'icon');
      document.head.appendChild(linkElement);
    }
    
    // Update href
    linkElement.setAttribute('href', iconUrl);
  });
};

/**
 * Removes all existing Twitter meta tags to avoid conflicts
 */
const removeExistingTwitterTags = (): void => {
  const twitterTags = document.querySelectorAll('meta[name^="twitter:"], meta[property^="twitter:"]');
  twitterTags.forEach(tag => tag.remove());
};

/**
 * Adds a Twitter meta tag with both name and property attributes
 * @param name The name/property of the Twitter tag
 * @param content The content to set
 */
const addTwitterTag = (name: string, content: string): void => {
  // Create tag with name attribute
  const nameTag = document.createElement('meta');
  nameTag.setAttribute('name', name);
  nameTag.setAttribute('content', content);
  document.head.appendChild(nameTag);
  
  // Create duplicate tag with property attribute for maximum compatibility
  const propertyTag = document.createElement('meta');
  propertyTag.setAttribute('property', name);
  propertyTag.setAttribute('content', content);
  document.head.appendChild(propertyTag);
};

/**
 * Generates a URL for the dynamic meta tags API
 * This can be used to create shareable links that will display correct OG tags
 * when shared on social media platforms
 * 
 * @param partner Partner data
 * @param customLogo Custom logo URL
 * @param customBrandingImage Custom branding image URL
 * @returns URL to use for sharing on social media
 */
export const generateSharingUrl = (partner: ServiceProvider | null, customLogo: string | null, customBrandingImage: string | null): string => {
  if (!partner) return window.location.href;
  
  // Get business name or display name
  const businessName = encodeURIComponent(partner.businessProfile?.businessName || partner.display_name || 'HoSaksham');
  
  // Create description from partner data
  const description = encodeURIComponent(partner.plundrDescription || 
    `${businessName} - Manage your subscriptions and grow your business with HoSaksham's Revenue Growth Management platform.`);
  
  // Get partner image or default to logo
  const imageUrl = encodeURIComponent(customLogo || customBrandingImage || 
    'https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FOg%20image.png?alt=media&token=6908d97d-8f83-4875-9508-e1e595889f95');
  
  // Create the URL for the meta tags API
  // This assumes you'll create an API endpoint at /api/meta-tags
  // that will generate HTML with the correct meta tags
  const currentUrl = encodeURIComponent(window.location.href);
  
  return `https://hosaksham.com/api/meta-tags?title=${businessName}&description=${description}&image=${imageUrl}&url=${currentUrl}`;
}; 